// src/Pages/DashboardAssistant/DashboardAssistantPage.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import DashboardCanvas from './DashboardCanvas';
import DashboardChatSidebar from './DashboardChatSidebar';
import { DashboardAssistantDataService } from '../../Services/DashboardAssistantDataService';
import './DashboardAssistant.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowLeftRotate, faArrowRight, faBrush, faEllipsis, faSave, faPlus, faImage, faFont } from '@fortawesome/free-solid-svg-icons';
import DashboardCanvasFlexible from './DashboardCanvasFlexible';
import { Modal, Form, Button } from 'react-bootstrap';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

const DEFAULT_LAYOUT = { width: 816, height: 1056 }; // Letter portrait ~96 DPI
const CANVAS_TEMPLATES = [
  { key: 'a5-portrait', label: 'A5 Portrait (560 x 794)', width: 560, height: 794 },
  { key: 'a5-landscape', label: 'A5 Landscape (794 x 560)', width: 794, height: 560 },
  { key: 'a4-portrait', label: 'A4 Portrait (794 x 1123)', width: 794, height: 1123 },
  { key: 'a4-landscape', label: 'A4 Landscape (1123 x 794)', width: 1123, height: 794 },
  { key: 'a3-portrait', label: 'A3 Portrait (1123 x 1587)', width: 1123, height: 1587 },
  { key: 'a3-landscape', label: 'A3 Landscape (1587 x 1123)', width: 1587, height: 1123 },
  { key: 'letter-portrait', label: 'Letter Portrait (816 x 1056)', width: 816, height: 1056 },
  { key: 'letter-landscape', label: 'Letter Landscape (1056 x 816)', width: 1056, height: 816 },
  { key: 'legal-portrait', label: 'Legal Portrait (816 x 1344)', width: 816, height: 1344 },
  { key: 'legal-landscape', label: 'Legal Landscape (1344 x 816)', width: 1344, height: 816 },
  { key: 'tabloid-portrait', label: 'Tabloid Portrait (1056 x 1632)', width: 1056, height: 1632 },
  { key: 'tabloid-landscape', label: 'Tabloid Landscape (1632 x 1056)', width: 1632, height: 1056 },
  { key: 'executive-portrait', label: 'Executive Portrait (696 x 1008)', width: 696, height: 1008 },
  { key: 'executive-landscape', label: 'Executive Landscape (1008 x 696)', width: 1008, height: 696 },
  { key: 'statement-portrait', label: 'Statement Portrait (528 x 816)', width: 528, height: 816 },
  { key: 'statement-landscape', label: 'Statement Landscape (816 x 528)', width: 816, height: 528 },
];
const TEMPLATE_PRESETS = {
  '1x1': { rows: 1, cols: 1 },
  '2x1': { rows: 1, cols: 2 },
  '2x2': { rows: 2, cols: 2 },
  '3x2': { rows: 2, cols: 3 },
  '3x3': { rows: 3, cols: 3 }
};

const layoutFromTemplate = (templateKey) => ({ ...(TEMPLATE_PRESETS[templateKey] ?? DEFAULT_LAYOUT) });
const layoutToTemplate = (value) => {
  const rows = Number(value?.rows ?? DEFAULT_LAYOUT.rows);
  const cols = Number(value?.cols ?? DEFAULT_LAYOUT.cols);
  return `${cols}x${rows}`;
};

const parseLayout = (value) => {
  if (!value) return DEFAULT_LAYOUT;
  try {
    if (typeof value === 'string') {
      if (TEMPLATE_PRESETS[value]) return layoutFromTemplate(value);
      const parsedString = JSON.parse(value);
      if (typeof parsedString === 'string' && TEMPLATE_PRESETS[parsedString]) return layoutFromTemplate(parsedString);
      value = parsedString;
    }

    if (value && typeof value === 'object') {
      if (Number.isFinite(value.width) || Number.isFinite(value.height)) {
        return {
          width: Number(value.width) || DEFAULT_LAYOUT.width || 1200,
          height: Number(value.height) || DEFAULT_LAYOUT.height || 800
        };
      }
      if (value.template && TEMPLATE_PRESETS[value.template]) {
        return layoutFromTemplate(value.template);
      }
      const rows = Number(value.rows);
      const cols = Number(value.cols);
      if (Number.isFinite(rows) && Number.isFinite(cols) && rows > 0 && cols > 0) {
        return { rows, cols };
      }
    }
    return DEFAULT_LAYOUT;
  } catch (_) {
    return DEFAULT_LAYOUT;
  }
};

const normalizeLoadedTiles = (tiles = []) =>
  tiles.map((tile, index) => {
    let parsedPos = tile.positionJson ?? tile.PositionJson ?? null;
    if (typeof parsedPos === 'string') {
      try {
        parsedPos = JSON.parse(parsedPos);
      } catch (_) {
        parsedPos = null;
      }
    }
    return {
      id: tile.id ?? tile.tileId ?? tile.TileId ?? `tile-${index}`,
      tileId: tile.tileId ?? tile.TileId,
      title: tile.title ?? tile.Title ?? '',
      description: tile.description ?? tile.Description ?? '',
      sqlQuery: tile.sqlQuery ?? tile.SqlQuery ?? '',
      chartSpec: tile.chartSpec ?? tile.ChartSpec ?? {},
      data: tile.data ?? tile.Data ?? [],
      columns: tile.columns ?? tile.Columns ?? [],
      lastRunDate: tile.lastRunDate ?? tile.LastRunDate,
      tileType: tile.tileType ?? tile.TileType ?? 'chart',
      textContent: tile.textContent ?? tile.TextContent ?? '',
      imageUrl: tile.imageUrl ?? tile.ImageUrl ?? '',
      showTitle: tile.showTitle ?? tile.ShowTitle ?? true,
      showLegend: tile.showLegend ?? tile.ShowLegend ?? true,
      showAxis: tile.showAxis ?? tile.ShowAxis ?? true,
      showGrid: tile.showGrid ?? tile.ShowGrid ?? true,
      showFrame: tile.showFrame ?? tile.ShowFrame ?? true,
      position: parsedPos ?? { x: 20 + (index % 3) * 340, y: 20 + Math.floor(index / 3) * 260, width: 320, height: 240 }
    };
  });

const DashboardAssistantContent = () => {
  const { instance, accounts } = useMsal();
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [loadingView, setLoadingView] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [dashboardName, setDashboardName] = useState('My Dashboard');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tiles, setTiles] = useState([]); // {id, chartSpec, data, sqlQuery, description, title, position}
  const [layout, setLayout] = useState(DEFAULT_LAYOUT); // saved LayoutJson
  const [viewMode, setViewMode] = useState(false);
  const [exporting, setExporting] = useState(false);
  const gridRef = React.useRef(null);
  const [showCanvasGrid, setShowCanvasGrid] = useState(true);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [newTextTitle, setNewTextTitle] = useState('');
  const [newTextContent, setNewTextContent] = useState('');
  const [newTextEditorState, setNewTextEditorState] = useState(() => EditorState.createEmpty());
  const [newImageTitle, setNewImageTitle] = useState('');
  const [newImageFile, setNewImageFile] = useState(null);
  const [editingTileId, setEditingTileId] = useState(null);
  const [editingTileType, setEditingTileType] = useState(null);
  const dashboardId = searchParams.get('dashboardId');
  const TEMPLATE_OPTIONS = CANVAS_TEMPLATES;

  // const addTile = (tile) => setTiles((prev) => [...prev, { id: crypto.randomUUID(), ...tile }]);
  const addTile = (tile) =>
    setTiles((prev) => {
      const idx = prev.length;
      const col = idx % 3;
      const row = Math.floor(idx / 3);
      const basePosition = {
        x: 20 + col * 360,
        y: 20 + row * 280,
        width: 320,
        height: 240,
      };
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          position: basePosition,
          ...tile
        }
      ];
    });
  const removeTile = (id) => setTiles((prev) => prev.filter((t) => t.id !== id));

  const getToken = useCallback(async () => {
    const account = accounts?.[0];
    if (!account) throw new Error('No MSAL account available');
    const tokenRes = await instance.acquireTokenSilent({ ...loginRequest, account });
    return tokenRes.accessToken;
  }, [accounts, instance]);

  const loadExistingDashboard = useCallback(async () => {
    const account = accounts?.[0];
    if (!dashboardId || !account) return;
    setErrorMessage('');
    setStatusMessage('');
    setLoadingView(true);
    try {
      const tokenRes = await instance.acquireTokenSilent({ ...loginRequest, account });
      const response = await DashboardAssistantDataService.load(dashboardId, tokenRes.accessToken);
      const normalizedTiles = normalizeLoadedTiles(response?.tiles ?? response?.Tiles ?? []);
      setTiles(normalizedTiles);
      const nextLayout = parseLayout(response?.layoutJson ?? response?.LayoutJson);
      setLayout(nextLayout);
      const loadedName = response?.name ?? response?.Name;
      if (loadedName) setDashboardName(loadedName);
    } catch (err) {
      console.error('Failed to load dashboard for editing', err);
      setErrorMessage('Unable to load this dashboard. Try refreshing.');
    } finally {
      setLoadingView(false);
    }
  }, [dashboardId, accounts, instance]);

  const handleSave = async () => {
    setSaving(true);
    setStatusMessage('');
    setErrorMessage('');
    try {
      const token = await getToken();
      const payload = {
        name: dashboardName,
        layoutJson: JSON.stringify(layout),
      tiles: tiles.map((t, i) => ({
        tileId: t.tileId,
        title: t.title,
        sqlQuery: t.sqlQuery,
        chartSpec: t.chartSpec,
        description: t.description,
        tileType: t.tileType ?? 'chart',
        textContent: t.textContent ?? '',
        columns: t.columns ?? [],
        data: t.data ?? [],
        imageUrl: t.imageUrl ?? '',
        showTitle: t.showTitle ?? true,
        showLegend: t.showLegend ?? true,
        showAxis: t.showAxis ?? true,
        showGrid: t.showGrid ?? true,
        showFrame: t.showFrame ?? true,
        positionJson: JSON.stringify(t.position ?? { x: 0, y: 0, width: 320, height: 240 }),
          displayOrder: i
        }))
      };
      const id = dashboardId
        ? await DashboardAssistantDataService.update(dashboardId, payload, token)
        : await DashboardAssistantDataService.save(payload, token);
      setStatusMessage(`Saved dashboard #${id}`);
    } catch (err) {
      console.error('Failed to save dashboard', err);
      setErrorMessage('Unable to save the dashboard right now.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTextTile = () => {
    setNewTextTitle('Text tile');
    setNewTextContent('');
    setNewTextEditorState(EditorState.createEmpty());
    setEditingTileId(null);
    setEditingTileType('text');
    setShowTextModal(true);
  };

  const submitTextTile = () => {
    const htmlContent = draftToHtml(convertToRaw(newTextEditorState.getCurrentContent()));
    if (editingTileId) {
      setTiles((prev) =>
        prev.map((t) =>
          t.id === editingTileId
            ? {
              ...t,
              title: newTextTitle || t.title || 'Text tile',
              textContent: htmlContent || newTextContent || '',
              tileType: 'text',
            }
            : t
        )
      );
    } else {
      addTile({
        title: newTextTitle || 'Text tile',
        textContent: htmlContent || newTextContent || '',
        tileType: 'text',
        description: '',
        chartSpec: {},
        data: [],
        columns: []
      });
    }
    setShowTextModal(false);
    setEditingTileId(null);
    setEditingTileType(null);
  };

  const handleAddImageTile = () => {
    setNewImageTitle('Image tile');
    setNewImageFile(null);
    setEditingTileId(null);
    setEditingTileType('image');
    setShowImageModal(true);
  };

  const submitImageTile = async () => {
    if (!newImageFile) {
      if (editingTileId) {
        setTiles((prev) =>
          prev.map((t) =>
            t.id === editingTileId
              ? { ...t, title: newImageTitle || t.title || 'Image tile' }
              : t
          )
        );
      }
      setShowImageModal(false);
      setEditingTileId(null);
      setEditingTileType(null);
      return;
    }
    try {
      const token = await getToken();
      const res = await DashboardAssistantDataService.uploadImage(newImageFile, token);
      const imageUrl = res?.url || '';
      if (editingTileId) {
        setTiles((prev) =>
          prev.map((t) =>
            t.id === editingTileId
              ? { ...t, title: newImageTitle || t.title || 'Image tile', imageUrl, tileType: 'image' }
              : t
          )
        );
      } else {
        addTile({
          title: newImageTitle || newImageFile.name || 'Image tile',
          imageUrl,
          tileType: 'image',
          description: '',
          chartSpec: {},
          data: [],
          columns: []
        });
      }
    } catch (err) {
      console.error('Image upload failed', err);
      setErrorMessage('Image upload failed.');
    } finally {
      setShowImageModal(false);
      setEditingTileId(null);
      setEditingTileType(null);
    }
  };

  const handleExportPdf = async () => {
    if (!gridRef.current) {
      setErrorMessage('Nothing to export yet.');
      return;
    }
    setExporting(true);
    setErrorMessage('');
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      const element = gridRef.current;
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const width = layout?.width ?? canvas.width;
      const height = layout?.height ?? canvas.height;
      const orientation = width >= height ? 'l' : 'p';
      const pdf = new jsPDF({ orientation, unit: 'px', format: [width, height] });
      pdf.addImage(imgData, 'PNG', 0, 0, width, height);
      pdf.save(`${dashboardName || 'dashboard'}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF', err);
      setErrorMessage('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    loadExistingDashboard();
  }, [loadExistingDashboard]);

  const allFramesHidden = tiles.length > 0 && tiles.every((t) => t.showFrame === false);

  return (
    <div className="dashboard-assistant">
      <section className="assistant-panel assistant-details-panel">
        <div className="assistant-panel-header">
          <div className="w-50 dashboard-title-editor">
            {isEditingTitle ? (
              <input
                className="dashboard-title-input"
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                onBlur={() => setIsEditingTitle(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setIsEditingTitle(false);
                  }
                }}
                autoFocus
                placeholder="My Dashboard"
              />
            ) : (
              <h3
                className="dashboard-title-display"
                tabIndex={0}
                onClick={() => setIsEditingTitle(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsEditingTitle(true);
                  }
                }}
              >
                {dashboardName || 'Untitled dashboard'}
              </h3>
            )}
            {!tiles.length && (
              <div className="text-muted small mt-1">Tiles generated from your chat appear here.</div>
            )}
          </div>
          <div className="assistant-actions">
            <button
              className="btn btn-outline-primary no-wrap"
              onClick={loadExistingDashboard}
              disabled={!dashboardId || loadingView}
              title='Relaod canvas and data'
            >
              {loadingView ?
                <FontAwesomeIcon icon={faEllipsis} /> :
                <FontAwesomeIcon icon={faArrowLeftRotate} />
              }
            </button>
            <button
              className="btn btn-outline-secondary no-wrap"
              onClick={() => setTiles([])}
              disabled={!tiles.length}
              title='Clear all tiles'
            >
              <FontAwesomeIcon icon={faBrush} />
            </button>
            <button
              className={`btn btn-${allFramesHidden ? 'secondary' : 'outline-secondary'} no-wrap`}
              onClick={() => {
                if (!tiles.length) return;
                const hideFrames = !allFramesHidden;
                setTiles((prev) => prev.map((t) => ({ ...t, showFrame: !hideFrames })));
              }}
              disabled={!tiles.length}
              title={allFramesHidden ? 'Show all tile borders/backgrounds' : 'Hide all tile borders/backgrounds'}
            >
              {allFramesHidden ? 'Borders off' : 'Borders on'}
            </button>
            <button
              className={`btn btn-${showCanvasGrid ? 'secondary' : 'outline-secondary'} no-wrap`}
              onClick={() => setShowCanvasGrid((prev) => !prev)}
              title={showCanvasGrid ? 'Hide canvas grid' : 'Show canvas grid'}
            >
              {showCanvasGrid ? 'Grid on' : 'Grid off'}
            </button>
            <div className="btn-group" role="group" aria-label="Add tile buttons">
              <button
                className="btn btn-outline-primary no-wrap"
                onClick={handleAddTextTile}
                disabled={viewMode}
                title="Add text tile"
              >
                <FontAwesomeIcon icon={faFont} className="me-1" /> Text
              </button>
              <button
                className="btn btn-outline-primary no-wrap"
                onClick={handleAddImageTile}
                disabled={viewMode}
                title="Add image tile"
              >
                <FontAwesomeIcon icon={faImage} className="me-1" /> Image
              </button>
            </div>
            <button
              className="btn btn-outline-success no-wrap"
              onClick={handleSave}
              disabled={saving}
              title='Save current configuration'
            >
              {saving ?
                <FontAwesomeIcon icon={faEllipsis} /> :
                <FontAwesomeIcon icon={faSave} />
              }
            </button>
            <button
              className="btn btn-outline-secondary no-wrap"
              onClick={handleExportPdf}
              disabled={exporting}
              title='Export canvas to PDF'
            >
              {exporting ? 'Exporting…' : 'Export PDF'}
            </button>
            <button
              className={`btn btn-${viewMode ? 'secondary' : 'outline-secondary'} no-wrap`}
              onClick={() => setViewMode((v) => !v)}
              title={viewMode ? 'Switch to edit mode' : 'Switch to view mode'}
            >
              {viewMode ? 'Read-only' : 'Editing'}
            </button>
            <div className="input-group ms-2 layout-select-group" style={{ minWidth: 220 }}>
              <label className="input-group-text" htmlFor="canvasSizeSelect">Canvas</label>
              <select
                className="form-select"
                id="canvasSizeSelect"
                value={
                  TEMPLATE_OPTIONS.find((t) => t.width === layout.width && t.height === layout.height)?.key
                  ?? 'custom'
                }
                onChange={(e) => {
                  const tmpl = TEMPLATE_OPTIONS.find((t) => t.key === e.target.value);
                  if (tmpl) {
                    setLayout({ width: tmpl.width, height: tmpl.height });
                  }
                }}
              >
                <option value="custom" disabled>Custom size</option>
                {TEMPLATE_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {errorMessage &&
          <div className="alert alert-danger alert-dismissible py-1">
            {errorMessage}
            <button type="button" className="btn-close" onClick={() => setErrorMessage(null)} aria-label="Close"></button>
          </div>
        }
        {statusMessage &&
          <div className="alert alert-success alert-dismissible py-1">
            {statusMessage}
            <button type="button" className="btn-close" onClick={() => setStatusMessage(null)} aria-label="Close"></button>
          </div>
        }

        <div className="assistant-details-body">
          <div className="assistant-canvas-wrapper">
            {loadingView ? (
              <p className="empty-state">Loading dashboard...</p>
            ) : tiles.length ? (
              <DashboardCanvasFlexible
                tiles={tiles}
                removeTile={removeTile}
                layout={layout}
                setLayout={setLayout}
                showLayoutControl={false}
                setTiles={setTiles}
                readOnly={viewMode}
                gridRefExternal={gridRef}
                showGrid={showCanvasGrid}
                onEditTile={(tile) => {
                  if (!tile) return;
                  if (tile.tileType === 'text') {
                    setEditingTileId(tile.id);
                    setEditingTileType('text');
                    setNewTextTitle(tile.title || 'Text tile');
                    const blocks = convertFromHTML(tile.textContent || '');
                    const content = ContentState.createFromBlockArray(blocks.contentBlocks, blocks.entityMap);
                    setNewTextEditorState(EditorState.createWithContent(content));
                    setNewTextContent(tile.textContent || '');
                    setShowTextModal(true);
                  } else if (tile.tileType === 'image') {
                    setEditingTileId(tile.id);
                    setEditingTileType('image');
                    setNewImageTitle(tile.title || 'Image tile');
                    setNewImageFile(null);
                    setShowImageModal(true);
                  }
                }}
              />
            ) : (
              <p className="empty-state">Use the assistant chat to generate tiles and preview them here.</p>
            )}
          </div>
        </div>
      </section>

      {/* Add Text Tile Modal */}
      <Modal show={showTextModal} onHide={() => setShowTextModal(false)} centered size='xl'>
        <Modal.Header closeButton>
          <Modal.Title>Add text tile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={newTextTitle}
              onChange={(e) => setNewTextTitle(e.target.value)}
              placeholder="Text tile title"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Content</Form.Label>
            <div className="border rounded p-1" style={{ minHeight: 240 }}>
              <Editor
                editorState={newTextEditorState}
                onEditorStateChange={(next) => {
                  setNewTextEditorState(next);
                  setNewTextContent(draftToHtml(convertToRaw(next.getCurrentContent())));
                }}
                wrapperClassName="rdw-wrapper"
                editorClassName="rdw-editor"
                toolbarClassName="rdw-toolbar"
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTextModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitTextTile}>
            Add text tile
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Image Tile Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add image tile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={newImageTitle}
              onChange={(e) => setNewImageTitle(e.target.value)}
              placeholder="Image tile title"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Image file</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => setNewImageFile(e.target.files?.[0] ?? null)}
            />
            {newImageFile && <div className="small mt-2 text-muted">{newImageFile.name}</div>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitImageTile} disabled={!newImageFile}>
            Upload & add image
          </Button>
        </Modal.Footer>
      </Modal>

      {collapsed ? (
        <button
          className="btn btn-primary da-chat-fab"
          onClick={() => setCollapsed(false)}
          title="Open assistant chat"
        >
          Chat
        </button>
      ) : (
        <div className="da-chat-float">
          <DashboardChatSidebar addTile={addTile} collapse={() => setCollapsed(true)} />
        </div>
      )}
    </div>
  );
};

const DashboardAssistantPage = ({ title = 'Dashboard Creation Assistant' }) => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title={title}>
      <DashboardAssistantContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default DashboardAssistantPage;
